
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import * as echarts from 'echarts';
import IncludeUnappliedToggle from '@/components/IncludeUnappliedToggle.vue';
import settingx from '@/store/modules/settingx';
import {getTableName} from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';

@Component({
	components: {
		TreeMapFilterable,
		IncludeUnappliedToggle,
	},
})
export default class CustomerOverdueTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['customers', 'agents', 'dateAsOf'];

	public result: any = [];
	public series: echarts.ECharts[] | any = [];

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get includeUnapplied() {
		return settingx.now.includeUnapplied;
	}

	public get expensiveHook() {
		const {
			selectedAsOfDate,
			selectedAgents,
			selectedCustomers,
			includeUnapplied,
		} = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedCustomers,
			includeUnapplied,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('customerTrades');

		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['customerTrades']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const rows = await queryHead.rawQuery(`
			SELECT "CODE" as code,
				SUM(CASE WHEN "DOCTYPE" IN ('IV','DN') THEN TRY_CAST("LOCALOSAMOUNT" AS DOUBLE) ELSE 0 END) as os_amt,
				SUM(CASE WHEN "DOCTYPE" IN ('CN','CT','DP','PM') THEN TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE) ELSE 0 END) as minus_unapplied,
				SUM(CASE WHEN "DOCTYPE" = 'CF' THEN TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE) ELSE 0 END) as plus_unapplied
			FROM ${table}
			WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedAsOfDate}
				AND TRY_CAST("DUEDATE" AS BIGINT) <= ${this.selectedAsOfDate}
				${customerFilter}${agentFilter}
			GROUP BY "CODE"
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		this.result = [];
		for (const row of rows) {
			const osAmt = Number(row.os_amt) || 0;
			let value = osAmt;
			if (this.includeUnapplied) {
				const minusUnapplied = Number(row.minus_unapplied) || 0;
				const plusUnapplied = Number(row.plus_unapplied) || 0;
				value = osAmt - minusUnapplied + plusUnapplied;
			}
			this.result.push({
				name: gdbx.customerNames[row.code] || row.code,
				value,
			});
		}

		const stock = {
			name: 'Customer Overdue Amount',
			type: 'treemap',
			roam: 'move',
			width: '100%',
			drillDownIcon: '',
			leafDepth: 1,
			label: { color: '#000' },
			itemStyle: { gapWidth: 1, borderWidth: 1 },
			data: this.result.length > 50 ? this.result.splice(50, this.result.length - 50) : this.result,
		};

		this.series = [stock];

		this.generateLoadingText(1);
		this.saveHistory('result', 'series');
	}
}
